import {useCallback, useEffect, useMemo, useRef} from 'react';

import {useAnimation, useMotionValue} from 'framer-motion';

import {ECardThemesIds} from '@/dataSource/src/api/cards-theme/entities';

const START_ANIMATION_DELAY = 100;
const FLIP_CARD_DELAY = 150;
const STIFFNESS_ANIMATION = 120;
const DAMPING_ANIMATION = 20;
const MASS_ANIMATION = 3;
const DURATION_ANIMATION = 0.6;
const ROTATE_INITIAL_ANIMATION = 360;
const DURATION_INITIAL_ANIMATION = 1;
const Y_INITIAL_ANIMATION = [-15, 0];
const ROTATE_ANIMATION = 180;
const ORIGINAL_SCALE = 1;
const REDUCE_SCALE = 0.92;
const ORIGINAL_LABEL = 'Or';
const ORIGINAL_VALUE = 'riginal';
const MO_LABEL = 'Mo';
const MO_VALUE = 'mo';
const ANIMATED_TYPE = 'spring';
const REPEAT_TYPE = 'reverse';
const EASE_ANIMATION = 'easeInOut';

export enum E_CARD_NOTE {
  NONE = 'NONE',
  DIY = 'DIY',
  VOTED = 'VOTED',
}

type ConfirmCardThemeModalActionsProps = {
  cardThemeSelected: ECardThemesIds;
  open: boolean;
  onClose: () => void;
};

export const useActions = ({
  cardThemeSelected,
  open,
  onClose,
}: ConfirmCardThemeModalActionsProps) => {
  //#region [---- [ANIMATIONS] ----]
  const controls = useAnimation();
  const flipped = useRef<boolean>(false);

  const y = useMotionValue(0);
  const scale = useMotionValue(1);
  const rotateY = useMotionValue(0);
  const opacityFront = useMotionValue(1);
  const opacityBack = useMotionValue(0);

  const startAnimation = useCallback(() => {
    controls.start({
      rotateY: ROTATE_INITIAL_ANIMATION,
      scale: ORIGINAL_SCALE,
      transition: {
        type: ANIMATED_TYPE,
        stiffness: STIFFNESS_ANIMATION,
        damping: DAMPING_ANIMATION,
        mass: MASS_ANIMATION,
      },
    });
  }, [controls]);

  const hoverAnimation = useCallback(() => {
    controls.start({
      y: Y_INITIAL_ANIMATION,
      transition: {
        duration: DURATION_INITIAL_ANIMATION,
        repeat: Infinity,
        repeatType: REPEAT_TYPE,
        ease: EASE_ANIMATION,
      },
    });
  }, [controls]);

  const pressInAnimation = useCallback(() => {
    scale.set(REDUCE_SCALE);
  }, [scale]);

  const flipCard = useCallback(() => {
    flipped.current = !flipped.current;
    hoverAnimation();
    scale.set(ORIGINAL_SCALE);
    controls.start({
      rotateY: flipped.current ? ROTATE_ANIMATION : 0,
      transition: {
        type: ANIMATED_TYPE,
        stiffness: STIFFNESS_ANIMATION,
        damping: DAMPING_ANIMATION,
        mass: MASS_ANIMATION,
        duration: DURATION_ANIMATION,
      },
    });

    setTimeout(() => {
      opacityFront.set(flipped.current ? 0 : 1);
      opacityBack.set(flipped.current ? 1 : 0);
    }, FLIP_CARD_DELAY);
  }, [controls, hoverAnimation, opacityBack, opacityFront, scale]);

  useEffect(() => {
    if (!open || !cardThemeSelected) return;
    setTimeout(() => {
      startAnimation();
      hoverAnimation();
    }, START_ANIMATION_DELAY);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cardThemeSelected]);
  //#endregion

  //#region [---- [HOOKS] ----]
  const cardThemeLabel = useMemo(() => {
    const label = cardThemeSelected?.replace(MO_VALUE, '') ?? '';
    if (!label) return '';
    const finalLabel = `${MO_LABEL}${label === ORIGINAL_VALUE ? ORIGINAL_LABEL : label?.charAt(0).toUpperCase()}${label?.slice(1)}`;
    return finalLabel;
  }, [cardThemeSelected]);

  const cardFrontPicture = useMemo(
    () => `/assets/images/cards/MoCardFront-${cardThemeSelected}.webp`,
    [cardThemeSelected],
  );

  const cardBackPicture = useMemo(() => {
    switch (cardThemeSelected) {
      case ECardThemesIds.GOGH:
        return '/assets/images/cards/MoCardBack-blue.webp';
      case ECardThemesIds.BURST:
      case ECardThemesIds.FRESH:
        return '/assets/images/cards/MoCardBack-blueSky.webp';
      case ECardThemesIds.ZODIAC:
      case ECardThemesIds.CADE:
      case ECardThemesIds.TEXAS:
      case ECardThemesIds.OHIO:
      case ECardThemesIds.FOOTBALL:
      case ECardThemesIds.SUNSET:
      case ECardThemesIds.YELLOWSTONE:
      case ECardThemesIds.GAMING:
      case ECardThemesIds.ARCADE:
      case ECardThemesIds.PURPLE:
        return '/assets/images/cards/MoCardBack-black.webp';
      case ECardThemesIds.FACES:
      case ECardThemesIds.FLOW:
      case ECardThemesIds.STICKERS:
      case ECardThemesIds.CHARMINAR:
      case ECardThemesIds.DESERT:
      case ECardThemesIds.GREEN:
      default:
        return '/assets/images/cards/MoCardBack-common.webp';
    }
  }, [cardThemeSelected]);

  const cardNote = useMemo(() => {
    switch (cardThemeSelected) {
      case ECardThemesIds.PURPLE:
      case ECardThemesIds.GREEN:
        return E_CARD_NOTE.DIY;
      case ECardThemesIds.TEXAS:
      case ECardThemesIds.OHIO:
      case ECardThemesIds.FOOTBALL:
      case ECardThemesIds.SUNSET:
      case ECardThemesIds.YELLOWSTONE:
      case ECardThemesIds.GAMING:
      case ECardThemesIds.ARCADE:
      case ECardThemesIds.CHARMINAR:
      case ECardThemesIds.DESERT:
        return E_CARD_NOTE.VOTED;
      default:
        return E_CARD_NOTE.NONE;
    }
  }, [cardThemeSelected]);
  //#endregion

  //#region [---- [LOGIC] ----]
  const onCloseModal = useCallback(() => {
    y.set(0);
    scale.set(1);
    rotateY.set(0);
    opacityFront.set(1);
    opacityBack.set(0);
    onClose();
  }, [onClose, opacityBack, opacityFront, rotateY, scale, y]);
  //#endregion

  return {
    y,
    scale,
    rotateY,
    controls,
    opacityFront,
    opacityBack,
    cardThemeLabel,
    cardFrontPicture,
    cardBackPicture,
    cardNote,
    flipCard,
    pressInAnimation,
    onCloseModal,
  };
};
